// ─────────────────────────────────────────────────────────────────────────────
//  graphs/PieDonutChart.jsx
//
//  Self-contained:  transform  •  tooltips  •  interactive legend  •  render
//  ★ Fully responsive: chart + legend fill whatever card width/height is given
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import {
    ChartAnimationWrapper, ErrorMessage,
    getSeriesColors, getSingleUnit, formatNumberWithSuffix,
    BIOMEDICAL_WASTE_COLORS, DEFAULT_COLORS,
} from "../mainUnit";
import { error } from "jquery";

// ─── TRANSFORM ────────────────────────────────────────────────────────────────
const transformData = (rawData, groupedField, title) => {
    if (!rawData || rawData.length === 0) return null;

    const xAxisField = groupedField?.[0];
    const aggregated = {}, localUnitsMap = {}, colorMap = {};

    rawData.forEach(item => {
        const label = item[xAxisField] || 'Unknown';
        aggregated[label] = (aggregated[label] || 0) + (parseFloat(item.sum_value || item.min_value) || 0);
        if (item.unit) localUnitsMap[label] = item.unit;
    });

    const labels = Object.keys(aggregated);
    labels.forEach((lbl, idx) => {
        colorMap[lbl] = BIOMEDICAL_WASTE_COLORS[lbl] || DEFAULT_COLORS[idx % DEFAULT_COLORS.length];
    });

    return {
        graphType: 'pie', title: title || 'Distribution',
        labels,
        values:   Object.values(aggregated),
        colors:   labels.map(lbl => colorMap[lbl]),
        colorMap,
        unitsMap: localUnitsMap,
        series:   labels.map(lbl => ({ name: lbl, data: [aggregated[lbl]], color: colorMap[lbl] })),
    };
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmtShort = (val) => {
    if (val === null || val === undefined) return '—';
    const n = typeof val === 'number' ? val : parseFloat(val);
    return isNaN(n) ? '—' : formatNumberWithSuffix(n);
};

// ─── SPARK BAR ────────────────────────────────────────────────────────────────
const SparkBar = ({ pct, color }) => (
    <div style={{ height: 3, width: '100%', borderRadius: 999, background: '#f1f5f9', overflow: 'hidden', marginTop: 4 }}>
        <div style={{ height: '100%', width: `${Math.min(100, Math.max(0, pct))}%`, background: color, borderRadius: 999, transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)' }} />
    </div>
);

// ─── LEGEND ROW ───────────────────────────────────────────────────────────────
const LegendRow = ({ name, value, pct, color, unit, rank, hidden, onToggle, compact }) => (
    <div
        onClick={() => onToggle?.(name)}
        style={{ marginBottom: compact ? 8 : 12, cursor: 'pointer', opacity: hidden ? 0.38 : 1, transition: 'opacity 0.2s', paddingLeft: 2, paddingRight: 2 }}
        title={hidden ? `Show ${name}` : `Hide ${name}`}
    >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
                <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 800, fontFamily: "'DM Mono', monospace", color: '#cbd5e1', minWidth: 14, textAlign: 'right' }}>#{rank}</span>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: hidden ? '#cbd5e1' : color, flexShrink: 0, transition: 'background 0.2s' }} />
                <span style={{ fontSize: compact ? 11 : 12.5, fontWeight: 600, color: hidden ? '#94a3b8' : '#1e293b', fontFamily: "'DM Sans', system-ui", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', transition: 'color 0.2s', textDecoration: hidden ? 'line-through' : 'none' }}>
                    {name}
                </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, flexShrink: 0, marginLeft: 6 }}>
                <span style={{ fontSize: compact ? 12 : 13.5, fontWeight: 800, color: hidden ? '#94a3b8' : '#0f172a', fontFamily: "'DM Sans', system-ui", letterSpacing: '-0.02em', transition: 'color 0.2s' }}>{fmtShort(value)}</span>
                {unit && <span style={{ fontSize: 9, color: '#94a3b8', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase' }}>{unit}</span>}
                <span style={{ fontSize: 10, fontWeight: 700, color: hidden ? '#cbd5e1' : '#64748b', fontFamily: "'DM Mono', monospace", minWidth: 34, textAlign: 'right' }}>{pct.toFixed(1)}%</span>
            </div>
        </div>
        <SparkBar pct={pct} color={color} />
    </div>
);

// ─── TOOLTIP ──────────────────────────────────────────────────────────────────
const buildTooltip = (total, sharedUnit) => ({
    enabled: true,
    fillSeriesColor: false,
    style: { fontSize: '13px', fontFamily: "'DM Sans', system-ui" },
    custom: ({ series, seriesIndex, w }) => {
        const value     = series[seriesIndex];
        const label     = w.config.labels[seriesIndex];
        const color     = w.config.colors[seriesIndex];
        const pct       = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
        const formatted = fmtShort(value);
        const unitStr   = sharedUnit ? ` ${sharedUnit}` : '';

        // Rank by value descending
        const sorted = [...series].sort((a, b) => b - a);
        const rank   = sorted.indexOf(value) + 1;

        return `
            <div style="
                font-family: 'DM Sans', system-ui, sans-serif;
                background: #0f172a;
                border-radius: 12px;
                padding: 12px 14px;
                box-shadow: 0 8px 32px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.18);
                min-width: 168px;
                border: 1px solid rgba(255,255,255,0.08);
                position: relative;
                z-index: 99999;
            ">
                <!-- Header: color dot + label -->
                <div style="display:flex; align-items:center; gap:8px; margin-bottom:10px;">
                    <div style="
                        width: 10px; height: 10px; border-radius: 50%;
                        background: ${color};
                        box-shadow: 0 0 0 2px rgba(255,255,255,0.15);
                        flex-shrink: 0;
                    "></div>
                    <span style="
                        font-size: 12px; font-weight: 600; color: #e2e8f0;
                        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                        max-width: 140px;
                    ">${label}</span>
                </div>

                <!-- Divider -->
                <div style="height:1px; background: rgba(255,255,255,0.08); margin-bottom:10px;"></div>

                <!-- Value row -->
                <div style="display:flex; align-items:baseline; justify-content:space-between; gap:8px; margin-bottom:6px;">
                    <span style="font-size:11px; color:#64748b; font-weight:500; letter-spacing:0.04em; text-transform:uppercase;">Value</span>
                    <span style="font-size:15px; font-weight:800; color:#f8fafc; letter-spacing:-0.02em;">
                        ${formatted}${unitStr}
                    </span>
                </div>

                <!-- Share row -->
                <div style="display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:10px;">
                    <span style="font-size:11px; color:#64748b; font-weight:500; letter-spacing:0.04em; text-transform:uppercase;">Share</span>
                    <span style="font-size:13px; font-weight:700; color:${color};">${pct}%</span>
                </div>

                <!-- Mini progress bar -->
                <div style="height:4px; border-radius:999px; background:rgba(255,255,255,0.08); overflow:hidden;">
                    <div style="
                        height:100%; width:${pct}%;
                        background: linear-gradient(90deg, ${color}cc, ${color});
                        border-radius:999px;
                    "></div>
                </div>

                <!-- Rank badge -->
                <div style="margin-top:8px; display:flex; justify-content:flex-end;">
                    <span style="
                        font-size:9px; font-weight:700; color:#475569;
                        font-family:'DM Mono', monospace; letter-spacing:0.06em;
                    ">RANK #${rank}</span>
                </div>
            </div>
        `;
    },
});

// ─── RESPONSIVE HOOK ──────────────────────────────────────────────────────────
const useContainerSize = () => {
    const ref  = useRef(null);
    const [size, setSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect;
            setSize({ width: Math.floor(width), height: Math.floor(height) });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return [ref, size];
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const CustomPieDonut = ({ data, options = {}, isDonut }) => {
    const toolbarVisible = options.toolbarVisible !== false;
    const rawLabels  = data.labels  || [];
    const rawValues  = data.values  || [];
    const rawColors  = data.colors  || getSeriesColors(data.series || [], data.colorMap, data.colors);
    const unitsMap   = data.unitsMap || {};
    const sharedUnit = getSingleUnit(unitsMap);
    const rowUnit    = (name) => sharedUnit ? null : (unitsMap[name] || null);

    const sortedIdx = rawLabels.map((_, i) => i);
    const labels    = sortedIdx.map(i => rawLabels[i]);
    const values    = sortedIdx.map(i => rawValues[i]);
    const colors    = sortedIdx.map(i => rawColors[i]);

    const [hiddenSeries, setHiddenSeries] = useState({});
    const toggleSeries  = (name) => setHiddenSeries(prev => ({ ...prev, [name]: !prev[name] }));
    const visibleValues = values.map((v, i) => hiddenSeries[labels[i]] ? 0 : v);
    const total         = values.reduce((s, v) => s + v, 0);

    // ★ Measure the outer card container so we can calculate exact chart dimensions
    const [containerRef, { width: containerW, height: containerH }] = useContainerSize();

    // Layout decision:
    //   wide  (≥ 500px): chart left + legend right  →  side-by-side
    //   narrow (< 500px): chart top + legend below  →  stacked
    const isWide        = containerW >= 500;
    const LEGEND_W      = isWide ? Math.max(140, Math.min(220, containerW * 0.38)) : containerW;

    // Chart area available width/height
    const chartAreaW    = isWide ? containerW - LEGEND_W - 24 : containerW;
    const totalH        = options.height || containerH || 300;
    const chartAreaH    = isWide ? totalH : Math.min(totalH * 0.55, containerW);

    // ── NARROW: bigger chart — use 95% of container width, clamped 160–400
    // ── WIDE:   fit within the allocated chart area column as before
    const chartSize     = isWide
        ? Math.min(Math.max(120, Math.floor(Math.min(chartAreaW, chartAreaH) * 0.92)), 360)
        : Math.min(Math.max(160, Math.floor(containerW * 0.95)), 300);

    // Don't render until we have a real measurement
    const ready = containerW > 0;

    const chartConfig = {
        chart: {
            type: isDonut ? 'donut' : 'pie',
            fontFamily: "'DM Sans', system-ui, sans-serif",
            toolbar: { show: toolbarVisible ? true : false },
            animations: { enabled: true, easing: 'easeinout', speed: 900, animateGradually: { enabled: true, delay: 120 } },
        },
        colors, labels,
        stroke: { show: true, width: 2, colors: ['#ffffff'] },
        plotOptions: {
            pie: {
                expandOnClick: true,
                customScale: 0.95,
                donut: {
                    size: isDonut ? (options.donutSize || '62%') : '0%',
                    labels: {
                        show: isDonut,
                        total: {
                            show: true, label: 'Total',
                            fontSize: '11px', fontWeight: 600,
                            fontFamily: "'DM Sans', system-ui", color: '#94a3b8',
                            formatter: () => fmtShort(total),
                        },
                        value: {
                            show: true, fontSize: chartSize < 180 ? '12px' : '16px',
                            fontWeight: 700, fontFamily: "'DM Sans', system-ui",
                            color: '#0f172a', formatter: (v) => fmtShort(parseFloat(v)),
                        },
                    },
                },
            },
        },
        dataLabels: {
            enabled: chartSize >= 160,
            formatter: (val) => `${Number(val).toFixed(1)}%`,
            style: { fontSize: '11px', fontFamily: "'DM Mono', monospace", fontWeight: 700, colors: ['#ffffff'] },
            dropShadow: { enabled: true, top: 1, left: 1, blur: 3, color: '#000', opacity: 0.35 },
        },
        legend:  { show: false },
        tooltip: buildTooltip(total, sharedUnit),
        states: {
            hover:  { filter: { type: 'darken', value: 0.85 } },
            active: { allowMultipleDataPointsSelection: false, filter: { type: 'darken', value: 0.8 } },
        },
    };

    // ── NARROW legend: show 3 rows, then scroll  ──────────────────────────────
    // Each row is ~(compact ? 8 : 12)mb bottom + ~20px content ≈ 34px per row
    const ROW_H          = 38; // approximate px per legend row
    const NARROW_VISIBLE = 3;  // rows visible before scrolling in narrow mode
    const legendMaxH     = isWide
        ? totalH - 16                                   // wide: fill remaining height (unchanged)
        : NARROW_VISIBLE * ROW_H;                       // narrow: exactly 3 rows, rest scrolls

    // Compact rows when legend column is narrow
    const compact    = LEGEND_W < 180 || labels.length > 3;

    return (
        <ChartAnimationWrapper variant="zoomIn" duration={600}>
            {/* Outer ref div fills the card fully */}
            <div
                ref={containerRef}
                style={{ width: '100%', fontFamily: "'DM Sans', system-ui, sans-serif", boxSizing: 'border-box' }}
            >
                {ready && (
                    <div style={{
                        display: 'flex',
                        flexDirection: isWide ? 'row' : 'column',
                        // ── NARROW: center everything horizontally ──
                        alignItems: isWide ? 'center' : 'center',
                        gap: isWide ? 16 : 12,
                        padding: '4px 8px',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}>
                        {/* ── Chart ── */}
                        <div style={{
                            flexShrink: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            // In narrow mode, stretch to full width so centering works
                            width: isWide ? undefined : '100%',
                        }}>
                            <ReactApexChart
                                options={chartConfig}
                                series={visibleValues}
                                type={isDonut ? 'donut' : 'pie'}
                                height={chartSize}
                                width={chartSize}
                            />
                        </div>

                        {/* ── Legend ── */}
                        <div style={{
                            flex: isWide ? 1 : undefined,
                            width: isWide ? undefined : '100%',
                            minWidth: 0,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            maxHeight: legendMaxH,
                            paddingRight: 4,
                        }}>
                            {labels.map((name, i) => (
                                <LegendRow
                                    key={name} rank={i + 1} name={name}
                                    value={values[i]}
                                    pct={total > 0 ? (values[i] / total) * 100 : 0}
                                    color={colors[i % colors.length]}
                                    unit={rowUnit(name)}
                                    hidden={!!hiddenSeries[name]}
                                    onToggle={toggleSeries}
                                    compact={compact}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </ChartAnimationWrapper>
    );
};

// ─── PUBLIC EXPORTS ───────────────────────────────────────────────────────────
export const PieChart = ({ chartData, chartOptions = {}, groupedField = [] }) => {
    if(groupedField && groupedField.lengh > 1){
        return <ErrorMessage message="Pi And Donut Chart Does not Supports Stacking Please change your config " />
    }
    const data = chartOptions.transform
        ? transformData(chartData?.data, groupedField, chartData?.title)
        : chartData;

    if (data?.errorMessage) return <ErrorMessage message={data.errorMessage} />;
    if (!data?.values)      return <ErrorMessage />;
    return <CustomPieDonut data={data} options={chartOptions} isDonut={chartOptions?.type === 'donut'} />;
};

export const DonutChart = ({ chartData, chartOptions = {}, groupedField = [] }) => {
    const data = chartOptions.transform
        ? transformData(chartData?.data, groupedField, chartData?.title)
        : chartData;

    if (data?.errorMessage)    return <ErrorMessage message={data.errorMessage} />;
    if (!data?.series?.length) return <ErrorMessage message="No data available to display" />;
    return (
        <PieChart
            chartData={data}
            chartOptions={{ ...chartOptions, donutSize: chartOptions.donutSize || '62%', type: 'donut', transform: false }}
            groupedField={groupedField}
        />
    );
};